import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, errorText } from '../api/client'
import type { Lead, LeadBoard, LeadStage } from '../api/types'
import { AddContactModal } from './AddContactModal'
import { Icon } from './Icon'
import { CARD_MIME, dragProps, KanbanColumn } from './KanbanColumn'
import { Modal } from './Modal'
import { Empty, ErrorState, Loading } from './States'
import { useToast } from './Toast'
import { relativeDays, titleCase, usdShort } from '../lib/format'
import { stageColor, TONES } from '../lib/tint'
import { useAsync } from '../lib/useAsync'

import { equityOf } from '../lib/equity'


// Short forms of the intake timeline, for a card that has room for two words.
const TIMELINE_LABEL: Record<string, string> = {
  now: 'Ready now', '30_days': 'Within 30d', '90_days': '1–3 months',
  '6_months': '3–6 months', browsing: 'Looking',
}
const PAGE = 60

export function StatusBoard() {
  const [shown, setShown] = useState<Record<string, number>>({})
  const [dragging, setDragging] = useState(false)
  const [adding, setAdding] = useState<string | null>(null)
  // Lanes the branch added, e.g. "Credit repair" — each counts as one of the seven
  // standard statuses, so reports and drip filters still have an answer.
  const [lane, setLane] = useState<{ mode: 'add' } | { mode: 'rename'; stage: LeadStage } | null>(null)
  const [dragCol, setDragCol] = useState<{ from: number; to: number; startX: number; dx: number } | null>(null)
  // Where a dragged card would land: which column, and which slot in it.
  const [dropAt, setDropAt] = useState<{ status: string; index: number } | null>(null)
  const colRefs = useRef<(HTMLElement | null)[]>([])
  const { data, error, loading, reload } = useAsync(() => api.board('mine'), [])
  // Optimistic moves: applied locally at once, reverted if the server refuses.
  const [local, setLocal] = useState<LeadBoard | null>(null)
  const board = local ?? data
  const toast = useToast()

  const leads = board?.leads ?? []
  const stages = board?.stages ?? []

  const byStage = new Map<string, Lead[]>()
  stages.forEach((s) => byStage.set(s.key, []))
  leads.forEach((l) => { if (byStage.has(l.status)) byStage.get(l.status)!.push(l) })

  /** A card dropped in a column: it changes status if the column is new, and
   *  takes the slot it was dropped into. Arranging a column by hand overrides the
   *  scored order for that column — which is the point of dragging it. */
  async function drop(id: number, status: string, index?: number) {
    setDropAt(null)
    const current = board
    const lead = current?.leads.find((l) => l.id === id)
    if (!current || !lead) return
    if (lead.status === status) {
      if (index === undefined) return
      const column = (current.leads.filter((l) => l.status === status))
      const was = column.findIndex((l) => l.id === id)
      if (was === index || was === index - 1) return
      const reordered = [...column]
      const [moved] = reordered.splice(was, 1)
      reordered.splice(was < index ? index - 1 : index, 0, moved!)
      // Cards render in array order within their column, so the column is spliced
      // back in ahead of everyone else rather than sorted in place.
      setLocal({ ...current, leads: [...reordered, ...current.leads.filter((l) => l.status !== status)] })
      try {
        await api.setCardOrder(reordered.map((l) => l.id))
        refresh()
      } catch (e) {
        setLocal(current)
        toast(errorText(e), { tone: 'danger' })
      }
      return
    }
    await move(id, status)
  }

  async function move(id: number, status: string) {
    const current = board
    const lead = current?.leads.find((l) => l.id === id)
    if (!current || !lead || lead.status === status) return
    const from = lead.status
    const bump = (totals: LeadBoard['totals'], key: string, by: number) => ({
      ...totals, [key]: { status: key, leads: Math.max(0, (totals[key]?.leads ?? 0) + by) },
    })
    setLocal({
      ...current,
      leads: current.leads.map((l) => (l.id === id ? { ...l, status, status_at: new Date().toISOString() } : l)),
      totals: bump(bump(current.totals, from, -1), status, 1),
    })
    try {
      await api.updateContact(id, { status })
      toast(`${lead.full_name ?? 'Contact'} → ${titleCase(status)}`, {
        tone: 'positive',
        action: { label: 'Undo', onClick: () => move(id, from) },
      })
    } catch (e) {
      setLocal(current)
      toast(errorText(e), { tone: 'danger' })
    }
  }

  const refresh = () => {
    setLocal(null)
    reload()
  }

  // Dragging a column header: the drop target is the first column whose middle is
  // right of the pointer, so a half-crossed column counts as passed.
  useEffect(() => {
    if (!dragCol) return
    const onMove = (e: PointerEvent) => {
      setDragCol((d) => {
        if (!d) return d
        // The dragged lane is translated under the pointer, so its own rect moves
        // with it — measuring it would make the drop target chase the cursor.
        // Only the lanes staying put decide where it lands.
        const settled = colRefs.current
          .map((el, i) => ({ i, r: el?.getBoundingClientRect() }))
          .filter((c) => c.r && c.i !== d.from)
        const next = settled.find((c) => e.clientX < c.r!.left + c.r!.width / 2)
        return { ...d, to: next ? next.i : colRefs.current.filter(Boolean).length, dx: e.clientX - d.startX }
      })
    }
    const onUp = () => setDragCol((d) => {
      if (d && d.to !== d.from && d.to !== d.from + 1) void reorder(d.from, d.to)
      return null
    })
    document.body.style.userSelect = 'none'
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => {
      document.body.style.userSelect = ''
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragCol?.from])

  /** An empty lane takes nothing with it, so removing one just happens — with an
   *  Undo in the toast. A lane with people in it isn't removed at all: their status
   *  is a decision someone made, and moving them is a separate act. */
  async function removeLane(stage: LeadStage, count: number) {
    if (count) {
      toast(`“${stage.label}” has ${count} ${count === 1 ? 'person' : 'people'} in it — drag them somewhere else first.`,
        { tone: 'warning' })
      return
    }
    try {
      if (stage.custom) {
        await api.deleteLeadStage(stage.key)
        toast(`Deleted “${stage.label}”`, {
          tone: 'positive',
          action: { label: 'Undo', onClick: () => void api.addLeadStage(stage.label).then(refresh) },
        })
      } else {
        const prefs = await api.leadStagePrefs()
        await api.setLeadStagePrefs({
          order: prefs.lead_stages.order,
          removed: [...new Set([...prefs.lead_stages.removed, stage.key])],
        })
        toast(`Took “${stage.label}” off your board`, {
          tone: 'positive',
          action: { label: 'Undo', onClick: () => void putBackLane(stage.key) },
        })
      }
      refresh()
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    }
  }

  async function putBackLane(key: string) {
    const prefs = await api.leadStagePrefs()
    await api.setLeadStagePrefs({
      order: prefs.lead_stages.order,
      removed: prefs.lead_stages.removed.filter((k) => k !== key),
    })
    refresh()
  }

  /** Drag a column by its header to reorder the board. Personal, like hiding:
   *  everyone arranges their own. Pointer events so a trackpad and a touchscreen
   *  behave the same. */
  async function reorder(from: number, to: number) {
    const keys = stages.map((s) => s.key)
    const [moved] = keys.splice(from, 1)
    keys.splice(from < to ? to - 1 : to, 0, moved!)
    const order = Object.fromEntries(keys.map((k, i) => [k, i]))
    setLocal((b) => (b ?? board) && { ...(b ?? board)!, stages: keys.map((k) => stages.find((s) => s.key === k)!) })
    try {
      await api.setLeadStagePrefs({ order, removed: (await api.leadStagePrefs()).lead_stages.removed })
      reload()
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
      refresh()
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="page-tight">
        <h2 className="h2" style={{ marginTop: 0 }}>Status board</h2>
        <p className="subline">
          Where your own contacts stand. Drag a card to change someone's status; click to open
          their contact. {leads.length} of them are here — a role with no lifecycle, like a title
          company, has no funnel position and so no column to sit in. Campaign leads stay
          under <Link to="/leads">Leads</Link> until you take one on.
        </p>
      </div>

      {loading && !board ? (
        <Loading label="Loading your board…" />
      ) : error ? (
        <div className="page"><ErrorState error={error} onRetry={refresh} /></div>
      ) : leads.length === 0 ? (
        <div className="page">
          <Empty title="Nobody on your board yet">
            <button className="btn sm" onClick={() => setAdding('new')}>Add a contact</button>
          </Empty>
        </div>
      ) : (
        <>
        <div className="board-wrap">
          <div className="board">
            {stages.map((s) => {
              const rows = byStage.get(s.key) ?? []
              const terminal = s.key === 'dead' || s.key === 'funded'
              const limit = shown[s.key] ?? PAGE
              const at = stages.indexOf(s)
              return (
                <div key={s.key} style={{ display: 'contents' }}>
                {dragCol && dragCol.to === at ? <div className="lane-line" aria-hidden="true" /> : null}
                <KanbanColumn
                  innerRef={(el) => { colRefs.current[at] = el }}
                  className={dragCol?.from === at ? 'lane-dragging' : ''}
                  style={dragCol?.from === at ? { transform: `translateX(${dragCol.dx}px)` } : undefined}
                  label={`${s.label}, ${rows.length}`}
                  onDrop={(id) => drop(Number(id), s.key,
                    dropAt?.status === s.key ? dropAt.index : undefined)}
                  header={
                    <header className="kcol-head"
                      onPointerDown={(e) => {
                        if ((e.target as HTMLElement).closest('button')) return
                        setDragCol({ from: stages.indexOf(s), to: stages.indexOf(s), startX: e.clientX, dx: 0 })
                      }}>
                      <div className="kcol-top">
                        <span className="kswatch" style={{ background: stageColor(s.key, terminal) }} />
                        <span className="kcol-name">{s.label}</span>
                        <span className="kcol-count">{board?.totals[s.key]?.leads ?? 0}</span>
                        <span className="spacer" />
                        {s.custom ? (
                          <button className="icon-btn-plain" onClick={() => setLane({ mode: 'rename', stage: s })}
                            title={`Rename “${s.label}”`} aria-label={`Rename ${s.label}`}>
                            <Icon name="edit" size={16} />
                          </button>
                        ) : null}
                        <button className="icon-btn-plain" onClick={() => removeLane(s, rows.length)}
                          title={s.custom ? `Delete “${s.label}”` : `Remove “${s.label}” from your board`}
                          aria-label={`Remove ${s.label}`}>
                          <Icon name="delete" size={16} />
                        </button>
                        <button className="icon-btn-plain" onClick={() => setAdding(s.key)} title={`Add a contact in ${s.label}`}>
                          <Icon name="add" size={16} />
                        </button>
                      </div>
                      <div className="kcol-sub">
                        <span className="kcol-value">{usdShort(rows.reduce((a, l) => a + (equityOf(l) ?? 0), 0))}</span>
                        <span className="kcol-aging">
                          {dragging ? 'drop here'
                            : s.custom ? `counts as ${titleCase(s.rolls_up_to)}`
                              : rows.length ? 'equity' : 'empty'}
                        </span>
                      </div>
                    </header>
                  }
                >
                  <div className="kcol-body">
                    {rows.slice(0, limit).map((l, ci) => (
                      <div key={l.id}
                        onDragOver={(e) => {
                          if (!e.dataTransfer.types.includes(CARD_MIME)) return
                          e.preventDefault()
                          const r = e.currentTarget.getBoundingClientRect()
                          setDropAt({ status: s.key, index: e.clientY < r.top + r.height / 2 ? ci : ci + 1 })
                        }}>
                      {dropAt?.status === s.key && dropAt.index === ci ? <div className="card-dropline" /> : null}
                      <Link
                        to={`/contacts/c/${l.id}`}
                        className="deal"
                        style={{ color: 'inherit', display: 'block' }}
                        {...dragProps(String(l.id), (d) => { setDragging(d); if (!d) setDropAt(null) })}
                      >
                        <div className="deal-top">
                          <span className="deal-name">{l.full_name ?? '—'}</span>
                          <span className="spacer" />
                          {l.rank !== null ? <span className="chip-mono" title="Lead score">{l.rank}</span> : null}
                        </div>
                        <div className="deal-amt">{usdShort(equityOf(l))}</div>
                        <div className="deal-product">
                          {[l.address, l.city].filter(Boolean).join(', ') || '—'}
                        </div>
                        {/* What they said they want, when intake captured it — the
                            reason to call this card before the one below it. */}
                        {l.purpose || l.timeline ? (
                          <div className="deal-chips">
                            {l.purpose ? <span className="chip-micro" style={TONES.accent}>{titleCase(l.purpose)}</span> : null}
                            {l.timeline ? (
                              <span className="chip-micro" style={l.timeline === 'now' || l.timeline === '30_days' ? TONES.positive : TONES.neutral}>
                                {TIMELINE_LABEL[l.timeline] ?? titleCase(l.timeline)}
                              </span>
                            ) : null}
                          </div>
                        ) : null}
                        <div className="deal-foot">
                          <span><Icon name="schedule" size={13} /> {relativeDays(l.status_at)}</span>
                          <span>{l.owner ?? l.product ?? ''}</span>
                        </div>
                        <div className="deal-move" onClick={(e) => e.preventDefault()}>
                          <select
                            className="field"
                            value={l.status}
                            aria-label={`Move ${l.full_name ?? 'contact'} to stage`}
                            onChange={(e) => move(l.id, e.target.value)}
                          >
                            {stages.map((o) => <option key={o.key} value={o.key}>Move to {o.label}</option>)}
                          </select>
                        </div>
                      </Link>
                      {dropAt?.status === s.key && dropAt.index === ci + 1 && ci === rows.slice(0, limit).length - 1
                        ? <div className="card-dropline" /> : null}
                      </div>
                    ))}
                    {rows.length > limit ? (
                      <button
                        className="btn sm"
                        style={{ width: '100%', justifyContent: 'center' }}
                        onClick={() => setShown((m) => ({ ...m, [s.key]: limit + PAGE }))}
                      >
                        Show {Math.min(PAGE, rows.length - limit)} more of {rows.length - limit}
                      </button>
                    ) : null}
                  </div>
                </KanbanColumn>
                </div>
              )
            })}
            {dragCol && dragCol.to >= stages.length ? <div className="lane-line" aria-hidden="true" /> : null}
            <button className="kcol-add" onClick={() => setLane({ mode: 'add' })}
              title="Add a lane of your own, e.g. Credit repair">
              <Icon name="add" size={18} />
              <span>Add a lane</span>
            </button>
          </div>
        </div>
        {board?.elsewhere?.length ? (
          <div className="board-foot">
            {/* The board stops at the application; this is where those people went. */}
            {board.elsewhere.map((e) => (
              <span key={e.key} className="board-foot-lane">
                <strong>{e.leads}</strong>
                <span>{e.label === 'Funded' ? 'funded' : 'applied'} — <Link to="/pipeline">followed in the LOS Pipeline</Link></span>
              </span>
            ))}
          </div>
        ) : null}
        </>
      )}

      {lane ? (
        <LaneModal lane={lane} onClose={() => setLane(null)} onSaved={refresh}
          removed={board?.removed ?? []} onPutBack={putBackLane} />
      ) : null}

      <AddContactModal
        open={adding !== null}
        initialStatus={adding ?? 'new'}
        onClose={() => { setAdding(null); refresh() }}
      />
    </div>
  )
}


/** Add, rename or remove a lane on the status board. A new lane says which standard
 *  status it counts as, so every report, drip filter and stop rule still has an
 *  answer for the people in it — and removing one puts them back there. */
function LaneModal({ lane, removed, onPutBack, onClose, onSaved }: {
  lane: { mode: 'add' } | { mode: 'rename'; stage: LeadStage }
  /** Standard lanes taken off this board, offered back when adding one. */
  removed: { key: string; label: string }[]
  onPutBack: (key: string) => Promise<void>
  onClose: () => void
  onSaved: () => void
}) {
  const existing = lane.mode === 'rename' ? lane.stage : null
  const [label, setLabel] = useState(existing?.label ?? '')
  const [busy, setBusy] = useState(false)
  const toast = useToast()

  async function run(fn: () => Promise<unknown>, done: string) {
    setBusy(true)
    try {
      await fn()
      toast(done, { tone: 'positive' })
      onSaved()
      onClose()
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
      setBusy(false)
    }
  }

  return (
    <Modal open width={520} onClose={onClose}
      title={existing ? `Rename “${existing.label}”` : 'Add a lane'}
      subtitle={existing ? undefined : 'A column of your own on the status board.'}
      footer={
        <>
          <span className="spacer" />
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={busy || !label.trim()}
            onClick={() => (existing
              ? run(() => api.renameLeadStage(existing.key, label.trim()), 'Lane renamed')
              : run(() => api.addLeadStage(label.trim()), `Added “${label.trim()}”`))}>
            {existing ? 'Save' : 'Add lane'}
          </button>
        </>
      }>
      <div className="stack">
        <label className="fl">
          <span>Lane name</span>
          <input className="field" autoFocus value={label} maxLength={40}
            placeholder="e.g. Credit repair" onChange={(e) => setLabel(e.target.value)} />
        </label>
        {!existing ? (
          <p className="quiet" style={{ margin: 0, fontSize: 12.5 }}>
            It becomes a column at the end of the board, and a status you can drag people into, filter a
            drip by, and start a workflow from.
          </p>
        ) : null}
        {!existing && removed.length ? (
          <>
            <div className="wf-cat" style={{ marginTop: 4 }}>OFF YOUR BOARD</div>
            {removed.map((r) => (
              <button key={r.key} className="card wf-choice" disabled={busy}
                onClick={() => run(() => onPutBack(r.key), `${r.label} is back on your board`)}>
                <Icon name="undo" size={18} style={{ color: 'var(--accent)' }} />
                <span>
                  <span style={{ fontWeight: 600, display: 'block' }}>{r.label}</span>
                  <span className="quiet" style={{ fontSize: 12.5 }}>Put this column back</span>
                </span>
              </button>
            ))}
          </>
        ) : null}
      </div>
    </Modal>
  )
}
